import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { Stack } from 'src/core/ui/Stack';
import { ContentId } from 'src/utils/constants/contentId';

type CountDownLabel = 'hours' | 'minutes' | 'seconds';
type CountDownUnit = {
  label: CountDownLabel;
  contentId: ContentId;
};

const CountDownUnits: CountDownUnit[] = [
  { label: 'hours', contentId: ContentId.Hours },
  { label: 'minutes', contentId: ContentId.Minutes },
  { label: 'seconds', contentId: ContentId.Seconds },
];

export const Countdown = ({
  targetDate,
  completeContentId,
  className,
}: {
  targetDate: number | Date;
  completeContentId?: ContentId;
  className?: string;
}) => {
  const calculateTimeLeft = useCallback((): Record<CountDownLabel, number> => {
    let difference = new Date(targetDate).getTime() - new Date().getTime();
    difference = Math.max(difference, 0);
    return {
      hours: Math.floor(difference / (1000 * 60 * 60)),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      // Check if countdown is complete
      if (Object.values(newTimeLeft).every((val) => val === 0) && !isComplete) {
        setIsComplete(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  });

  const formatTime = (time: number) => {
    return time < 10 ? `0${time}` : time;
  };

  return (
    <div className={className}>
      {isComplete ? (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
        >
          <Content id={completeContentId ?? ContentId.Done} />
        </motion.div>
      ) : (
        <Stack direction="row" gap="m">
          {CountDownUnits.map(({ label, contentId }) => (
            <Stack direction="row" gap="s" key={label}>
              <div>
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={timeLeft[label]}
                    initial={{ y: -30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 30, opacity: 0 }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                    }}
                  >
                    {formatTime(timeLeft[label] || 0)}
                  </motion.div>
                </AnimatePresence>
              </div>
              <Content id={contentId} />
            </Stack>
          ))}
        </Stack>
      )}
    </div>
  );
};
